import React from 'react';
import { History, AlertCircle, RefreshCw, ArrowRight } from 'lucide-react';
import { useCarrierHistory } from '../../hooks/useCarrierHistory';
import type {
  AuthorityEventType,
  AuthorityKind,
  AuthorityTimelineEvent,
} from '../../types/carrierHistory';

export interface AuthorityHistoryTimelineProps {
  dotNumber: number;
}

type Tone = 'good' | 'bad' | 'warn' | 'neutral';

/** Human-readable labels and severity tones for each authority span kind. */
const EVENT_TYPE_META: Record<AuthorityEventType, { label: string; tone: Tone }> = {
  granted: { label: 'Authority Granted', tone: 'good' },
  application_pending: { label: 'Application Pending', tone: 'warn' },
  revocation_pending: { label: 'Revocation Pending', tone: 'bad' },
  revoked: { label: 'Revoked', tone: 'bad' },
  suspended: { label: 'Suspended', tone: 'bad' },
  reinstated: { label: 'Reinstated', tone: 'good' },
  status_observed: { label: 'Status Observed', tone: 'neutral' },
};

const KIND_LABELS: Record<AuthorityKind, string> = {
  common: 'Common',
  contract: 'Contract',
  broker: 'Broker',
  unknown: 'Authority',
};

const TONE_DOT_CLASSES: Record<Tone, string> = {
  good: 'bg-green-100 border-green-500',
  bad: 'bg-red-100 border-red-500',
  warn: 'bg-yellow-100 border-yellow-500',
  neutral: 'bg-gray-100 border-gray-400',
};

const TONE_NEW_VALUE_CLASSES: Record<Tone, string> = {
  good: 'bg-green-100 text-green-700',
  bad: 'bg-red-100 text-red-700',
  warn: 'bg-yellow-100 text-yellow-700',
  neutral: 'bg-gray-100 text-gray-700',
};

function formatDate(value: string | null): string {
  if (!value) return '—';
  // 'YYYY-MM-DD' — construct as a local date to avoid UTC-midnight day shift.
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  const date = match
    ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    : new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatValue(value: string | null): string {
  return value === null || value === '' ? '—' : value;
}

const TimelineEvent: React.FC<{ event: AuthorityTimelineEvent; isLast: boolean }> = ({
  event,
  isLast,
}) => {
  const meta = EVENT_TYPE_META[event.eventType] ?? {
    label: event.eventType.replace(/_/g, ' '),
    tone: 'neutral' as Tone,
  };

  return (
    <li className="relative pl-8 pb-6">
      {/* Connector line */}
      {!isLast && (
        <span
          className="absolute left-[7px] top-5 bottom-0 w-px bg-gray-200"
          aria-hidden="true"
        />
      )}
      {/* Node dot */}
      <span
        className={`absolute left-0 top-1.5 h-4 w-4 rounded-full border-2 ${TONE_DOT_CLASSES[meta.tone]}`}
        aria-hidden="true"
      />

      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="text-sm font-semibold text-gray-900">
            {KIND_LABELS[event.authorityKind]} Authority · {meta.label}
          </span>
          <span className="text-xs text-gray-500">
            {formatDate(event.effectiveFrom)}
            {event.effectiveTo
              ? ` – ${formatDate(event.effectiveTo)}`
              : ' – Present'}
            {!event.dateIsExact && ' (approximate date)'}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700">
            {formatValue(event.priorValue)}
          </span>
          <ArrowRight size={14} className="text-gray-400" aria-hidden="true" />
          <span
            className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${TONE_NEW_VALUE_CLASSES[meta.tone]}`}
          >
            {formatValue(event.newValue)}
          </span>
        </div>

        <div className="text-xs text-gray-600">
          {event.authorityScope !== 'UNKNOWN' && (
            <>
              Docket: <span className="font-medium">{event.authorityScope}</span>
              {' · '}
            </>
          )}
          Source: <span className="font-medium">{event.sourceTable}</span>
        </div>
      </div>
    </li>
  );
};

const LoadingSkeleton: React.FC = () => (
  <ul className="animate-pulse">
    {[0, 1, 2].map((i) => (
      <li key={i} className="relative pl-8 pb-6">
        {i < 2 && (
          <span className="absolute left-[7px] top-5 bottom-0 w-px bg-gray-200" aria-hidden="true" />
        )}
        <span className="absolute left-0 top-1.5 h-4 w-4 rounded-full bg-gray-200" aria-hidden="true" />
        <div className="flex flex-col gap-2">
          <div className="h-4 w-48 rounded bg-gray-200" />
          <div className="h-4 w-56 rounded bg-gray-200" />
          <div className="h-3 w-32 rounded bg-gray-100" />
        </div>
      </li>
    ))}
  </ul>
);

/**
 * Chronological timeline of a carrier's reconstructed operating-authority
 * spans (grants, pending applications/revocations, revocations, suspensions
 * and reinstatements), backed by
 * GET /api/carrier-history/authority/:dotNumber.
 */
const AuthorityHistoryTimeline: React.FC<AuthorityHistoryTimelineProps> = ({ dotNumber }) => {
  const { data, isLoading, error, refetch } = useCarrierHistory(dotNumber, 'authority');

  const events = data?.events ?? [];
  const hasNoData = data !== null && (data.status !== 'ok' || events.length === 0);

  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4 sm:p-6">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <History size={20} className="text-gray-700" aria-hidden="true" />
          <h3 className="text-xl font-semibold text-gray-900">Authority History</h3>
        </div>
        {!isLoading && !error && (
          <button
            type="button"
            onClick={() => refetch()}
            className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-md px-2.5 py-1.5 bg-white"
            aria-label="Refresh authority history"
          >
            <RefreshCw size={14} aria-hidden="true" />
            Refresh
          </button>
        )}
      </div>

      {isLoading && <LoadingSkeleton />}

      {!isLoading && error && (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
          <AlertCircle size={20} className="text-red-600 mt-0.5" aria-hidden="true" />
          <div>
            <p className="text-sm font-semibold text-red-700">
              Unable to load authority history
            </p>
            <p className="text-sm text-red-600">{error}</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="mt-2 inline-flex items-center gap-1.5 text-sm text-red-700 underline"
            >
              <RefreshCw size={14} aria-hidden="true" />
              Try again
            </button>
          </div>
        </div>
      )}

      {!isLoading && !error && hasNoData && (
        <div className="flex flex-col items-center gap-2 py-8 text-center">
          <History size={32} className="text-gray-300" aria-hidden="true" />
          <p className="text-base text-gray-700 font-medium">
            No authority changes recorded
          </p>
          <p className="text-sm text-gray-500">
            {data?.reason ??
              'Authority grants, revocations, suspensions and reinstatements will appear here once they are recorded for this carrier.'}
          </p>
        </div>
      )}

      {!isLoading && !error && !hasNoData && (
        <>
          <p className="text-sm text-gray-600 mb-4">
            {events.length} recorded span{events.length === 1 ? '' : 's'} (oldest first)
          </p>
          <ul>
            {events.map((event, index) => (
              <TimelineEvent
                key={`${event.authorityKind}-${event.authorityScope}-${event.eventType}-${event.effectiveFrom}-${index}`}
                event={event}
                isLast={index === events.length - 1}
              />
            ))}
          </ul>
          {data !== null && data.unparseableDates > 0 && (
            <p className="text-xs text-gray-500 mt-2">
              {data.unparseableDates} source date{data.unparseableDates === 1 ? '' : 's'} could
              not be parsed and {data.unparseableDates === 1 ? 'was' : 'were'} skipped.
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default AuthorityHistoryTimeline;
